import { useState, useLayoutEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import AmbientBackground from '../components/ui/AmbientBackground';
import CareerlyLogo from '../assets/CareerlyLogo.png';

const Login = () => {
    const { lockDarkMode, unlockDarkMode } = useTheme();

    // FORCE dark mode - lock it so it can NEVER be changed to light on this page
    useLayoutEffect(() => {
        lockDarkMode();
        return () => unlockDarkMode(); // Restore user's preference when leaving
    }, [lockDarkMode, unlockDarkMode]);

    const { loginWithGoogle, signInWithEmail, signUpWithEmail } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isSignUp, setIsSignUp] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleEmailAuth = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        const { error } = isSignUp
            ? await signUpWithEmail(email, password)
            : await signInWithEmail(email, password);

        setLoading(false);
        if (error) {
            setError(error.message);
        } else {
            navigate('/dashboard');
        }
    };

    const handleGoogleLogin = async () => {
        setLoading(true);
        const { error } = await loginWithGoogle();
        setLoading(false);
        if (error) setError(error.message);
    };

    const styles = {
        container: {
            minHeight: '100vh',
            display: 'flex',
            position: 'relative',
        },
        // Left side - Branding
        brandingSide: {
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            padding: 'var(--space-4xl)',
            position: 'relative',
            zIndex: 1,
        },
        logo: {
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-sm)',
            marginBottom: 'var(--space-4xl)',
        },
        logoMark: {
            width: '48px',
            height: '48px',
        },
        logoText: {
            fontFamily: 'var(--font-display)',
            fontSize: '2rem',
            fontWeight: 600,
            background: 'var(--gradient-primary)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
        },
        brandingTitle: {
            fontFamily: 'var(--font-display)',
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            fontWeight: 700,
            lineHeight: 1.2,
            marginBottom: 'var(--space-xl)',
            color: 'var(--text-primary)',
        },
        brandingGradient: {
            background: 'linear-gradient(135deg, var(--indigo-400) 0%, var(--cyan-300) 50%, var(--amber-400) 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            backgroundSize: '200% auto',
            animation: 'gradient-shift 8s linear infinite',
        },
        brandingSubtitle: {
            fontSize: '1.1rem',
            color: 'var(--text-secondary)',
            lineHeight: 1.7,
            maxWidth: '400px',
        },
        features: {
            marginTop: 'var(--space-4xl)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-lg)',
        },
        featureItem: {
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-md)',
            color: 'var(--text-secondary)',
            fontSize: '0.95rem',
        },
        featureIcon: {
            width: '24px',
            height: '24px',
            background: 'var(--gradient-primary)',
            borderRadius: 'var(--radius-sm)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
        },
        // Right side - Form
        formSide: {
            width: '500px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 'var(--space-4xl)',
            background: 'rgba(15, 15, 26, 0.8)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            borderLeft: '1px solid var(--border-subtle)',
            position: 'relative',
            zIndex: 1,
        },
        formContainer: {
            width: '100%',
            maxWidth: '380px',
        },
        formTitle: {
            fontFamily: 'var(--font-display)',
            fontSize: '1.75rem',
            fontWeight: 700,
            marginBottom: 'var(--space-sm)',
            color: 'var(--text-primary)',
        },
        formSubtitle: {
            color: 'var(--text-tertiary)',
            marginBottom: 'var(--space-2xl)',
            fontSize: '0.95rem',
        },
        googleBtn: {
            width: '100%',
            padding: 'var(--space-md)',
            background: 'var(--gradient-primary)',
            color: 'white',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            fontSize: '1rem',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 'var(--space-sm)',
            transition: 'all var(--duration-base) var(--ease-out)',
            boxShadow: 'var(--glow-indigo)',
        },
        divider: {
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-md)',
            margin: 'var(--space-xl) 0',
            color: 'var(--text-muted)',
        },
        dividerLine: {
            flex: 1,
            height: '1px',
            background: 'var(--border-subtle)',
        },
        input: {
            width: '100%',
            padding: 'var(--space-md)',
            marginBottom: 'var(--space-md)',
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-light)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--text-primary)',
            fontSize: '0.95rem',
            outline: 'none',
            transition: 'all var(--duration-fast)',
            fontFamily: 'var(--font-body)',
        },
        errorBox: {
            padding: 'var(--space-md)',
            marginBottom: 'var(--space-md)',
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--red-500)',
            fontSize: '0.9rem',
        },
        submitBtn: {
            width: '100%',
            padding: 'var(--space-md)',
            background: 'var(--bg-surface)',
            color: 'var(--text-primary)',
            border: '1px solid var(--border-light)',
            borderRadius: 'var(--radius-md)',
            fontSize: '1rem',
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'all var(--duration-fast)',
        },
        switchText: {
            textAlign: 'center',
            marginTop: 'var(--space-xl)',
            color: 'var(--text-tertiary)',
            fontSize: '0.9rem',
        },
        switchBtn: {
            background: 'none',
            border: 'none',
            color: 'var(--indigo-400)',
            cursor: 'pointer',
            textDecoration: 'underline',
            fontSize: '0.9rem',
            fontWeight: 600,
        },
    };

    const keyframes = `
        @keyframes gradient-shift {
            0% { background-position: 0% center; }
            100% { background-position: 200% center; }
        }
    `;

    const CheckIcon = () => (
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M4 7l2 2 4-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
    );

    return (
        <>
            <style>{keyframes}</style>
            <AmbientBackground variant="landing" />

            <div style={styles.container}>
                {/* Left Side - Branding */}
                <div style={styles.brandingSide}>
                    <div style={styles.logo}>
                        <img
                            src={CareerlyLogo}
                            alt="Careerly"
                            style={{
                                height: '48px',
                                width: 'auto',
                                objectFit: 'contain'
                            }}
                        />
                    </div>

                    <h1 style={styles.brandingTitle}>
                        Your Career Journey,<br/>
                        <span style={styles.brandingGradient}>Supercharged by AI</span>
                    </h1>

                    <p style={styles.brandingSubtitle}>
                        Join thousands of job seekers who've transformed their career search with AI-powered tools.
                    </p>

                    <div style={styles.features}>
                        <div style={styles.featureItem}>
                            <div style={styles.featureIcon}><CheckIcon /></div>
                            <span>AI-powered job matching & discovery</span>
                        </div>
                        <div style={styles.featureItem}>
                            <div style={styles.featureIcon}><CheckIcon /></div>
                            <span>Generate tailored resumes in seconds</span>
                        </div>
                        <div style={styles.featureItem}>
                            <div style={styles.featureIcon}><CheckIcon /></div>
                            <span>Mock interviews with AI feedback</span>
                        </div>
                        <div style={styles.featureItem}>
                            <div style={styles.featureIcon}><CheckIcon /></div>
                            <span>Track progress & build streaks</span>
                        </div>
                    </div>
                </div>

                {/* Right Side - Login Form */}
                <div style={styles.formSide}>
                    <div style={styles.formContainer}>
                        <h2 style={styles.formTitle}>
                            {isSignUp ? 'Create Account' : 'Welcome Back'}
                        </h2>
                        <p style={styles.formSubtitle}>
                            {isSignUp
                                ? 'Start your journey to landing your dream job'
                                : 'Continue your career journey'
                            }
                        </p>

                        <button
                            onClick={handleGoogleLogin}
                            disabled={loading}
                            style={styles.googleBtn}
                            onMouseOver={(e) => {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 0 50px rgba(99, 102, 241, 0.4)';
                            }}
                            onMouseOut={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = 'var(--glow-indigo)';
                            }}
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                            </svg>
                            <span>{loading ? 'Signing in...' : 'Continue with Google'}</span>
                        </button>

                        <div style={styles.divider}>
                            <div style={styles.dividerLine} />
                            <span style={{ fontSize: '0.85rem' }}>or</span>
                            <div style={styles.dividerLine} />
                        </div>

                        <form onSubmit={handleEmailAuth}>
                            <input
                                type="email"
                                placeholder="Email address"
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                required
                                style={styles.input}
                                onFocus={(e) => {
                                    e.target.style.borderColor = 'var(--indigo-500)';
                                    e.target.style.boxShadow = '0 0 0 3px rgba(99, 102, 241, 0.1)';
                                }}
                                onBlur={(e) => {
                                    e.target.style.borderColor = 'var(--border-light)';
                                    e.target.style.boxShadow = 'none';
                                }}
                            />
                            <input
                                type="password"
                                placeholder="Password"
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                                required
                                style={styles.input}
                                onFocus={(e) => {
                                    e.target.style.borderColor = 'var(--indigo-500)';
                                    e.target.style.boxShadow = '0 0 0 3px rgba(99, 102, 241, 0.1)';
                                }}
                                onBlur={(e) => {
                                    e.target.style.borderColor = 'var(--border-light)';
                                    e.target.style.boxShadow = 'none';
                                }}
                            />

                            {error && (
                                <div style={styles.errorBox}>
                                    {error}
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={loading}
                                style={styles.submitBtn}
                                onMouseOver={(e) => {
                                    e.currentTarget.style.background = 'var(--bg-elevated)';
                                    e.currentTarget.style.borderColor = 'var(--indigo-500)';
                                }}
                                onMouseOut={(e) => {
                                    e.currentTarget.style.background = 'var(--bg-surface)';
                                    e.currentTarget.style.borderColor = 'var(--border-light)';
                                }}
                            >
                                {loading ? 'Please wait...' : (isSignUp ? 'Create Account' : 'Sign In')}
                            </button>
                        </form>

                        <p style={styles.switchText}>
                            {isSignUp ? 'Already have an account?' : "Don't have an account?"}{' '}
                            <button
                                onClick={() => setIsSignUp(!isSignUp)}
                                style={styles.switchBtn}
                            >
                                {isSignUp ? 'Sign In' : 'Sign Up'}
                            </button>
                        </p>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Login;
